import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AccountService } from '../account/account.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TransferBalanceService } from './transfer-balance.service';
import { TransferBalanceModel } from './transfer-balance.model';
import { AuthService } from '../../shared/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { FormatCurrencyPipe } from 'src/app/shared/pipes/formatCurrency.pipe';

@Component({
  selector: 'app-transfer-balance',
  templateUrl: './transfer-balance.html',
  styleUrls: ['./transfer-balance.scss'],
  providers: [FormatCurrencyPipe]
})
export class TransferBalanceComponent {
  transferBalance = new FormGroup({
    conta: new FormControl<string>('', Validators.required),
    valorSolicitado: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(10),
    ]),
  });

  public saldoDisponivel: string = '';

  public placeholderValue: string;

  public isSubmitted: boolean;
  public documentFeedBack: string;
  public chavePixFeedBack: string;
  public valorFeedBack: string;
  public contaFeedBack: string;
  public nomePagador: string;
  public nomeRecebedor: string;
  public isLoaded: boolean = true;
  public openConfirm: boolean;
  public errorMessage: string;
  public validationCode: string;
  public dictResponse: any;
  public tefAccounts: { id: string; description?: string; originCurrency?: string }[] = [];
  public validatingTotp: boolean = false;

  @ViewChild('requestTOTPCode') validateToken: ElementRef | any;

  constructor(
    private accountService: AccountService,
    private transferBalanceService: TransferBalanceService,
    private authService: AuthService,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private fmt: FormatCurrencyPipe
  ) {
    this.getAccountValues();
    this.isSubmitted = false;
    this.documentFeedBack = '';
    this.chavePixFeedBack = '';
    this.valorFeedBack = '';
    this.contaFeedBack = '';
    this.nomeRecebedor = '';
    this.nomePagador = '';
    this.placeholderValue = '';
    this.errorMessage = '';
    this.validationCode = '';
    this.openConfirm = false;
  }

  ngOnInit(): void {
    this.loadAccount();
  }

  private loadAccount(): void {
    this.accountService.getAccount().then(acc => {
      const list = acc?.listsAccountTef ?? [];

      this.tefAccounts = list.map((it: any) => ({
        id: it.id,
        description: it.description,
        originCurrency: it.originCurrency,
      }));
    });
  }

  getAccountValues() {
    this.accountService.getAccount().then((value) => {
      this.saldoDisponivel = value.readyAmount;
      this.getPlaceHolder();
    });
  }

  copyId(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.toastr.success('Id copiado com sucesso!', 'Sucesso', {
        timeOut: 2000,
        positionClass: 'toast-top-right'
      });
    }).catch(err => {
      this.toastr.error('Erro ao copiar Id', 'Erro', {
        timeOut: 2000,
        positionClass: 'toast-top-right'
      });
    });
  }

  async paste(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();

      this.transferBalance.controls.conta.setValue(text.trim());

    } catch (err) {
      console.error('Erro ao colar o código:', err);
    }
  }

  async pasteTotpCode(): Promise<void> {
    try {
      this.validationCode = (await navigator.clipboard.readText()).trim();
    } catch (err) {
      console.error('Erro ao colar o código:', err);
    }
  }

  getPlaceHolder(): void {
    const valorDisponivel = Number(this.saldoDisponivel);
    let placeholder = valorDisponivel < 0 ? 0 : valorDisponivel;

    this.placeholderValue = this.fmt.transform(placeholder);
  }

  valorSolicitadoChanged() {
    let valor = Number(
      Number(this.transferBalance.controls.valorSolicitado.value).toFixed(2)
    );
  }

  openDataConfirm() {
    this.isSubmitted = true;
    this.isLoaded = false;
    if (!this.formIsValid()) {
      this.isLoaded = true;
      return;
    }
    this.transferBalanceService
      .searchAccount(this.transferBalance.controls.conta.value!)
      .then((value) => {
        this.dictResponse = value;
        this.nomePagador = this.dictResponse.ownerPayerName;
        this.nomeRecebedor = this.dictResponse.ownerName;
        this.isLoaded = true;

        if (this.dictResponse.isValidated) {
          this.makeFundTransfer();
        } else {
          this.openConfirm = true;
        }
      })
      .catch((error) => {
        this.toastr.error(error.error.mensagem, 'Erro', {
          timeOut: 3000,
          positionClass: 'toast-top-right',
        });
        this.isLoaded = true;
      });
  }

  makeFundTransfer() {
    if (this.authService.isReadOnly()) {
      this.toastr.error('Seu usuário não tem permissão para realizar essa operação!', 'Erro', {
        timeOut: 3000,
        positionClass: 'toast-top-right',
      });
      return;
    }

    this.modalService.open(this.validateToken, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'sm',
    });
  }

  public clearForm() {
    this.transferBalance.reset();
    this.accountService.getAccount().then((value) => {
      this.saldoDisponivel = value.readyAmount;
      this.getPlaceHolder();
      this.isSubmitted = false;
      this.openConfirm = false;
    });
  }

  public formIsValid(): boolean {
    if (this.transferBalance.controls.valorSolicitado.hasError('min')) {
      this.valorFeedBack = 'Valor inválido - Valor mínimo R$ 10,00!';
    } else if (this.transferBalance.controls.valorSolicitado.invalid) {
      this.valorFeedBack = 'Valor é obrigatório!';
    }

    if (this.transferBalance.controls.conta.invalid) {
      this.contaFeedBack = 'Conta é obrigaória!'
    }
    return this.transferBalance.valid;
  }

  async validateTotp() {

    if (this.validatingTotp) {
      return;
    }

    this.validatingTotp = true;

    if (await this.isValidTotp()) {
      this.modalService.dismissAll();
      let transferBalanceModel = new TransferBalanceModel();
      transferBalanceModel.value = this.transferBalance.controls.valorSolicitado.value!;
      transferBalanceModel.accountId = this.transferBalance.controls.conta.value!;
      transferBalanceModel.payerName = this.nomePagador! + " - " + this.nomeRecebedor!;
      firstValueFrom(this.transferBalanceService.sendFunds(transferBalanceModel))
        .then((value) => {
          if (value.isSend) {
            this.toastr.success(
              `Solicitação de transferência realizada com sucesso! Transação: ${value.id}`,
              'Sucesso',
              {
                timeOut: 3000,
                positionClass: 'toast-center-center',
              }
            );
          }
          else {
            this.toastr.warning(
              `Solicitação de transação em analise!`,
              'Alerta',
              {
                timeOut: 3000,
                positionClass: 'toast-center-center',
              }
            );
          }

          this.clearForm();
          this.isLoaded = true;
          this.validatingTotp = false;
        })
        .catch((error) => {
          this.toastr.error(error.error.mensagem, 'Erro', {
            timeOut: 3000,
            positionClass: 'toast-top-right',
          });
          this.isLoaded = true;
          this.validatingTotp = false;
        });
    } else {
      this.errorMessage = 'Código de autenticação inválido!';
      this.validatingTotp = false;
    }
  }

  async isValidTotp() {
    let isValid: boolean = false;
    await this.authService
      .validateTotpCode(this.validationCode)
      .then((value) => {
        isValid = value.isValid;
      });
    return isValid;
  }
}
